import { useEffect, useMemo, useRef, useState } from "preact/hooks";
import { useTranslation } from "react-i18next";
import { com } from "../../proto/game.pb";
import { useWebSocket } from "../../shared/contexts";
import { IGameMessage, MT } from "../../shared/types/proto/mt";

export type LeaderboardMetric = "x" | "win" | "rounds";
export type LeaderboardPeriod = "day" | "month" | "year";
export type RoundsDetailsTab = "all-bets" | "my-bets" | "leaderboard";

const HIST_PAGE_SIZE = 20;
const LB_PAGE_SIZE = 100;

export function useRoundsDetails() {
  const { onMessage, sendProto, status } = useWebSocket();
  const { t } = useTranslation();

  // UI state
  const [activeTab, setActiveTab] = useState<RoundsDetailsTab>("all-bets");
  const [metric, setMetric] = useState<LeaderboardMetric>("x");
  const [period, setPeriod] = useState<LeaderboardPeriod>("day");

  // Leaderboard state
  const [lbLoading, setLbLoading] = useState(false);
  const [lbEntries, setLbEntries] = useState<
    | com.achlid.crash.game.stateless.ILeaderboardEntry[]
    | com.achlid.crash.game.stateless.ITopRoundEntry[]
  >([]);

  // Bet history state
  const [histLoading, setHistLoading] = useState(false);
  const [histEntries, setHistEntries] = useState<
    com.achlid.crash.game.stateless.IBetHistoryEntry[]
  >([]);
  const [histPage, setHistPage] = useState(0);
  const [histPageSize, setHistPageSize] = useState(HIST_PAGE_SIZE);
  const [histTotal, setHistTotal] = useState<number | null>(null);
  const [histHasMore, setHistHasMore] = useState<boolean>(false);
  const lastRequestedPageRef = useRef(0);

  const tableHeader = [
    { label: t("rounds_details.player"), alignment: "left" },
    { label: "X", alignment: "center" },
    { label: t("rounds_details.round_x"), alignment: "center" },
    { label: t("rounds_details.bet_usd"), alignment: "center" },
    { label: t("rounds_details.win_usd"), alignment: "right" },
  ];
  const tableRoundsHeader = [
    { label: t("rounds_details.date_time"), alignment: "left", flex: 2 },
    { label: t("rounds_details.x"), alignment: "center", flex: 1 },
    { label: "", alignment: "right", flex: 1 },
  ];
  const tableBetHeader = [
    { label: t("rounds_details.player"), alignment: "left" },
    { label: t("rounds_details.bet"), alignment: "center" },
    { label: "X", alignment: "center" },
    { label: t("rounds_details.win_usd"), alignment: "right" },
  ];

  const tabs = [
    { id: "all-bets", label: t("rounds_details.all_bets") },
    { id: "my-bets", label: t("rounds_details.my_bets") },
    { id: "leaderboard", label: t("rounds_details.leaderboard") },
  ];

  const tabsLeaderboard = [
    { id: "x", label: "X" },
    { id: "win", label: t("rounds_details.win") },
    { id: "rounds", label: t("rounds_details.rounds") },
  ];
  const tabsPeriods = [
    { id: "day", label: t("rounds_details.day") },
    { id: "month", label: t("rounds_details.month") },
    { id: "year", label: "Year" },
  ];

  const periodCode = useMemo(() => {
    switch (period) {
      case "day":
        return 0; // DAY
      case "month":
        return 1; // MONTH
      case "year":
        return 2; // YEAR
      default:
        return 0;
    }
  }, [period]);

  const sortByCode = useMemo(() => {
    switch (metric) {
      case "x":
        return 0; // BY_MULTIPLIER
      case "win":
        return 1; // BY_WINS
      case "rounds":
        return 2; // BY_ROUNDS
      default:
        return 0;
    }
  }, [metric]);

  const requestLeaderboard = useMemo(() => {
    return () => {
      if (status !== "open") return;
      setLbLoading(true);
      const req: IGameMessage = {
        type: MT.GET_LEADERBOARD,
        getLeaderboard: {
          type: periodCode,
          sortBy: sortByCode,
          limit: LB_PAGE_SIZE,
        },
      };
      sendProto(req);
    };
  }, [status, periodCode, sortByCode, sendProto]);

  const requestHistory = useMemo(() => {
    return (page = histPage, pageSize = histPageSize) => {
      if (status !== "open") return;
      setHistLoading(true);
      lastRequestedPageRef.current = page;
      const req: IGameMessage = {
        type: MT.GET_BET_HISTORY,
        getBetHistory: { page, pageSize },
      };
      sendProto(req);
    };
  }, [status, histPage, histPageSize, sendProto]);

  useEffect(() => {
    const offLb = onMessage(MT.LEADERBOARD_RESPONSE, (msg) => {
      const resp = msg.leaderboardResponse;
      if (metric !== "rounds") {
        setLbEntries(resp?.entries ?? []);
      } else {
        setLbEntries(resp?.topRounds ?? []);
      }

      setLbLoading(false);
    });
    const offHist = onMessage(MT.BET_HISTORY_RESPONSE, (msg) => {
      const resp = msg.betHistoryResponse;
      if (resp) {
        const entries = Array.isArray(resp.entries) ? resp.entries : [];
        setHistEntries((prev) =>
          lastRequestedPageRef.current > 0 ? [...prev, ...entries] : entries
        );
        if (resp.totalCount != null) setHistTotal(Number(resp.totalCount));
        setHistHasMore(Boolean((resp as any)?.hasMore));
      }
      setHistLoading(false);
    });
    return () => {
      offLb();
      offHist();
    };
  }, [onMessage, metric]);

  useEffect(() => {
    if (activeTab === "leaderboard") requestLeaderboard();
  }, [activeTab, periodCode, sortByCode]);

  // Reset my-bets data on tab enter to avoid duplicates when revisiting
  useEffect(() => {
    if (activeTab !== "my-bets") return;
    setHistEntries([]);
    setHistPage(0);
    setHistHasMore(false);
    setHistTotal(null);
    lastRequestedPageRef.current = 0;
  }, [activeTab]);

  useEffect(() => {
    if (activeTab === "my-bets") requestHistory(histPage, histPageSize);
  }, [activeTab, histPage, histPageSize, requestHistory]);
  const loadMore = useMemo(() => {
    return () => {
      if (!histLoading && histHasMore) setHistPage((p) => p + 1);
    };
  }, [histLoading, histHasMore]);

  return {
    tableHeaders: {
      tableHeader,
      tableRoundsHeader,
      tableBetHeader,
      tabs,
      tabsLeaderboard,
      tabsPeriods,
    },
    // tabs
    activeTab,
    setActiveTab,
    // leaderboard
    metric,
    setMetric,
    period,
    setPeriod,
    lbLoading,
    lbEntries,
    refreshLeaderboard: requestLeaderboard,
    // history
    histLoading,
    histEntries,
    histPage,
    setHistPage,
    histPageSize,
    setHistPageSize,
    histTotal,
    histHasMore,
    loadMore,
    refreshHistory: requestHistory,
  } as const;
}
